import React, { useEffect, useRef, useState } from 'react';
import { Image } from 'antd';
import { Text, BoldText, SpaceEnd } from '../../../../../styles/common.styles';
import { black, blueLink } from '../../../../../styles/colors';
import { useAppDispatch, useAppSelector } from '../../../../../hooks/redux';
import {
    getLiveGuidelineProductsAction,
} from '../../../../../redux/actions/liveGuidelineActions';
import {
    LiveGuidelineCriterion,
    LiveGuidelineProduct,
} from '../../../../../redux/actions/liveGuidelineActions/types';
import { ActionResponse } from '../../../../../models/actionResponse/types';
import {
    ProductDateFilter,
    ProductFilters,
} from '../../../../../components/products/productsFilterModal/ProductsFilterModal';
import ProductsFilterModal from '../../../../../components/products/productsFilterModal/ProductsFilterModal';
import ProductsFilterButton from '../../../../../components/products/productsFilterModal/ProductsFilterButton';
import ProductsSearch from '../../../../../components/products/productsTable/ProductsSearch';
import { BrandCompany, ExternalProduct, Product } from '../../../../../models/product/types';
import {
    GetProductsFromTransparencyCatalogAPIResponse,
    ProductsFilterData,
} from '../../../../../redux/actions/transparencyCatalogActions/types';
import {
    getCompaniesOfProducts,
    getExternalProductsAction,
} from '../../../../../redux/actions/transparencyCatalogActions';
import { flattenTcMfSectionProducts } from '../../../../../helpers/tcExternalProducts';
import { mergeInternalProductsWithExternal } from '../../../../../helpers/mergeProjectProducts';
import { toGuidelineInternalProduct } from '../../../../../helpers/liveGuidelineProducts';
import { normalizeRating, RATING_SYSTEM_TITLE_MAP } from '../../../../../constants/ratingSystems';
import {
    matchesMasterFormatSearch,
    MASTERFORMAT_SEARCH_PLACEHOLDER,
} from '../../../../../helpers/masterFormatSearch';
import { MaterialsFiltersIds } from '../../../../../types/materialsFilters.enum';
import { Division, Section } from '../../../../../models/productDivision/types';
import { LiveGuidelineProductTagsProvider } from './liveGuidelineProductTagsContext/LiveGuidelineProductTagsContext';
import { fetchLiveGuidelineTagsInBatches } from '../../../../../helpers/liveGuidelineProductTagBatchHelper';
import {
    DivisionsContainer,
    NoProductsTitleContainer,
    NoProductsTitleContainerFiltered,
    ProductSpin,
    ProductsTabContent,
    ProductsTabPageContainer,
    OpenCloseAllButton,
    FoundResultsCount,
    ProductFilterContainer,
    SelectAllContainer,
    SelectAllCheckbox,
} from '../../../../projectInfo/tabs/productsTab/ProductsTab.styles';
import {
    BannerWrapper,
    ProductsTabHeader,
    NoCriteriaButton,
    NoCriteriaState,
    NoCriteriaText,
} from './ProductsTab.styles';
import GuidelineDivisionItem from './guidelineDivisionItem/GuidelineDivisionItem';
import {
    AddProductButton,
    AddProductButtonContainer,
} from '../../../../../components/common/productsAction/ProductsAction.styles';
import { CheckboxChangeEvent } from 'antd/lib/checkbox';
import LiveGuidelineCriteriaBanner from './banner/LiveGuidelineCriteriaBanner';
import { openCatalogForLiveGuideline } from '../../../../../helpers/liveGuidelineRedirect';
import LiveGuidelineNotificationBanner from './banner/LiveGuidelineNotificationBanner';
import {
    addProductsFromLiveGuidelineAction,
    getProductReferencesAction,
} from '../../../../../redux/actions/productActions';
import { ProductReferences } from '../../../../../redux/actions/productActions/types';

interface Props {
    liveGuidelineId: number;
    liveGuidelineName: string;
    canHideProducts: boolean;
    onNavigateToNotifications: () => void;
    guidelineCriteria: LiveGuidelineCriterion[];
    isGuidelineLoading: boolean;
    isSyncInProgress: boolean;
    copyProductsTo?: string;
    canManageGuidelines: () => boolean;
}

interface State {
    products: Array<Product>;
    pending: boolean;
    searchString: string;
}

const matchesDateFilter = (
    dateAdded: string | null | undefined,
    dateFilter: ProductDateFilter,
): boolean => {
    if (!dateAdded) {
        return false;
    }

    const addedAt = Date.parse(dateAdded);
    if (!Number.isFinite(addedAt)) {
        return false;
    }

    const now = new Date();
    const cutoff = new Date(now);
    switch (dateFilter) {
        case 'last7Days':
            cutoff.setDate(cutoff.getDate() - 7);
            break;
        case 'last30Days':
            cutoff.setDate(cutoff.getDate() - 30);
            break;
        case 'last6Months':
            cutoff.setMonth(cutoff.getMonth() - 6);
            break;
        case 'lastYear':
            cutoff.setFullYear(cutoff.getFullYear() - 1);
            break;
    }

    return addedAt >= cutoff.getTime() && addedAt <= now.getTime();
};

const hasActiveFilters = (filters: ProductFilters): boolean =>
    Object.values(filters).some((filter) => Array.isArray(filter) && filter.length > 0);

const ProductsTab: React.FC<Props> = ({
    liveGuidelineId,
    liveGuidelineName,
    canHideProducts,
    onNavigateToNotifications,
    guidelineCriteria,
    isGuidelineLoading,
    isSyncInProgress,
    copyProductsTo,
    canManageGuidelines,
}) => {
    const dispatch = useAppDispatch();
    const { liveGuidelineReducer, ec3Reducer } = useAppSelector((state) => state);

    const [allDivisions, setAllDivisions] = useState<Division<Product>[]>([]);
    const [filteredDivisions, setFilteredDivisions] = useState<Division<Product>[]>([]);
    const [isFilterMode, setFilterMode] = useState(false);
    const [isFilteringInProgress, setFilteringInProgress] = useState(false);
    const [filterModalOpen, setFilterModalOpen] = useState(false);
    const [openedSectionsIds, setOpenedSectionsIds] = useState<string[]>([]);
    const [selectAll, setSelectAll] = useState(false);
    const [selectedProducts, setSelectedProducts] = useState<Product[]>([]);
    const [addingToProject, setAddingToProject] = useState(false);
    const [brandsInformation, setBrandsInformation] = useState<BrandCompany[]>([]);
    const [productsExternal, setProductsExternal] = useState<ExternalProduct[]>([]);
    const [references, setReferences] = useState<ProductReferences | null>(null);
    const [filters, setFilters] = useState<ProductFilters>({
        ratingSystems: [],
        epdFilters: [],
        smEpdsFilters: [],
        ec3Filters: [],
        regulationsFilters: [],
        materialsFilters: [],
        lowEmittingFilters: [],
        lemRegulationsFilters: [],
        tags: [],
        dateFilters: [],
    });
    const [state, setState] = useState<State>({
        products: [],
        pending: true,
        searchString: '',
    });

    const productsLoadRequestId = useRef(0);

    useEffect(() => {
        loadData();
    }, [liveGuidelineId]);

    useEffect(() => {
        if (liveGuidelineReducer.productsChangedAt > 0) {
            setProductsExternal([]);
            setBrandsInformation([]);
            setState((prev) => ({ ...prev, pending: true }));
            loadData();
        }
    }, [liveGuidelineReducer.productsChangedAt]);

    const loadData = async (filtersToSet?: ProductFilters, searchString?: string) => {
        setState((prev) => ({ ...prev, pending: true }));

        const { payload } = (await dispatch(
            getLiveGuidelineProductsAction(liveGuidelineId),
        )) as ActionResponse<LiveGuidelineProduct[]>;

        const lgProducts: LiveGuidelineProduct[] = payload?.data || [];

        const internalProducts = lgProducts
            .filter((lgp) => lgp.productId != null)
            .map(toGuidelineInternalProduct) as any[];

        await getProductReferences();
        await getProducts(internalProducts, filtersToSet, searchString);
    };

    const getProductReferences = async () => {
        const { payload } = (await dispatch(
            getProductReferencesAction(),
        )) as ActionResponse<ProductReferences>;
        setReferences(payload.data);
    };

    const getProducts = async (
        internalProducts: any[],
        filtersToSet?: ProductFilters,
        searchString?: string,
    ) => {
        const requestId = ++productsLoadRequestId.current;
        const productIds = internalProducts.map((p) => p.productId);

        if (!productIds.length) {
            if (requestId !== productsLoadRequestId.current) return;
            setState((s) => ({ ...s, products: [], pending: false }));
            setAllDivisions([]);
            return;
        }

        const needsExternalRefresh =
            !productsExternal.length ||
            internalProducts.length !== state.products.length ||
            productIds.some((pId) => !productsExternal.some((ep) => ep.id === pId));

        let availableProducts: ExternalProduct[] | null = productsExternal;
        if (needsExternalRefresh) {
            const { payload } = (await dispatch(
                getExternalProductsAction({ productIds }),
            )) as ActionResponse<GetProductsFromTransparencyCatalogAPIResponse>;
            availableProducts = payload.data
                ? flattenTcMfSectionProducts(payload.data.mfSection)
                : null;
        }

        if (requestId !== productsLoadRequestId.current) return;

        const resolvedExternalProducts = availableProducts ?? [];
        setProductsExternal(resolvedExternalProducts);

        const currentProducts = mergeInternalProductsWithExternal(
            internalProducts,
            resolvedExternalProducts,
        );
        
        const companies =
            needsExternalRefresh || !brandsInformation.length
                ? await getCompaniesOfProducts(currentProducts, dispatch)
                : brandsInformation;

        if (requestId !== productsLoadRequestId.current) return;

        setBrandsInformation(companies);

        const divisions = buildDivisions(currentProducts);
        setAllDivisions(divisions);

        if (requestId !== productsLoadRequestId.current) return;

        setState((s) => ({ ...s, products: currentProducts, pending: false }));

        const liveGuidelineProductIds = internalProducts
            .map((p) => p.id)
            .filter((id): id is number => id != null);
        if (liveGuidelineProductIds.length > 0) {
            fetchLiveGuidelineTagsInBatches(dispatch, liveGuidelineProductIds);
        }

        if (
            (filtersToSet &&
                Object.values(filtersToSet).some((f) => (Array.isArray(f) ? f.length > 0 : !!f))) ||
            searchString
        ) {
            const filtered = filterProducts(divisions, filtersToSet, searchString);
            setFilteredDivisions([...filtered].sort((a, b) => a.key - b.key));
            setFilterMode(true);
        }

        setFilteringInProgress(false);
    };

    const buildDivisions = (products: Product[]): Division<Product>[] => {
        const divisionsMap = new Map<number, any>();

        products.forEach((product) => {
            const divNum = product.mfDivision?.number;
            if (!divNum) return;
            const key = parseInt(divNum[0] + divNum[1]);
            const highestSection = product.mfSectionLevel1 || product.mfSection;
            if (!highestSection) return;
            const sectionKey = highestSection.number.slice(0, 5);

            if (!divisionsMap.has(key)) {
                divisionsMap.set(key, {
                    id: divNum,
                    name: product.mfDivision.label,
                    key,
                    clicked: false,
                    sections: new Map(),
                });
            }
            const div = divisionsMap.get(key);
            if (!div.sections.has(sectionKey)) {
                div.sections.set(sectionKey, {
                    id: sectionKey,
                    key: parseInt(highestSection.number[3] + highestSection.number[4]),
                    name: highestSection.label,
                    clicked: false,
                    products: [],
                });
            }
            div.sections.get(sectionKey).products.push(product);
        });

        for (const div of divisionsMap.values()) {
            div.sections = [...div.sections.values()].sort(
                (a: Section<Product>, b: Section<Product>) => a.key - b.key,
            );
        }

        return [...divisionsMap.values()].sort((a, b) => a.key - b.key);
    };

    // ─── Search ───────────────────────────────────────────────────────────────

    const changeSearch = (searchString: string) => {
        setState((s) => ({ ...s, searchString }));
    };

    const submitSearch = (e: any) => {
        setFilteringInProgress(true);
        const searchString = e.target.value;
        if (!searchString && !hasActiveFilters(filters)) {
            setFilterMode(false);
            setFilteredDivisions([]);
            setFilteringInProgress(false);
            return;
        }
        setFilterMode(true);
        const filtered = filterProducts(allDivisions, filters, searchString);
        setFilteredDivisions([...filtered].sort((a, b) => a.key - b.key));
        setFilteringInProgress(false);
    };

    const cleanFilter = () => {
        setFilteringInProgress(true);
        setState((s) => ({ ...s, searchString: '' }));
        if (!hasActiveFilters(filters)) {
            setFilterMode(false);
            setFilteredDivisions([]);
            setFilteringInProgress(false);
        } else {
            setFilterMode(true);
            const filtered = filterProducts(allDivisions, filters, '');
            setFilteredDivisions([...filtered].sort((a, b) => a.key - b.key));
            setFilteringInProgress(false);
        }
    };

    // ─── Filter ───────────────────────────────────────────────────────────────

    const handleFilters = (filtersToSet: ProductFilters) => {
        setFilteringInProgress(true);
        setFilters(filtersToSet);
        const hasFilters = hasActiveFilters(filtersToSet);
        if (!hasFilters && !state.searchString) {
            setFilterMode(false);
            setFilteredDivisions([]);
            setFilteringInProgress(false);
            return;
        }
        setFilterMode(true);
        const filtered = filterProducts(allDivisions, filtersToSet, state.searchString);
        setFilteredDivisions([...filtered].sort((a, b) => a.key - b.key));
        setFilteringInProgress(false);
    };

    const filterProducts = (
        divs: Division<Product>[],
        filtersToSet?: ProductFilters,
        searchString?: string,
    ): Division<Product>[] => {
        const hasFilters = filtersToSet && hasActiveFilters(filtersToSet);
        if (!hasFilters && !searchString) return divs;

        const strSearch = searchString ? searchString.toLowerCase() : null;
        const filteredDs: Division<Product>[] = [];

        for (const div of divs) {
            const filteredSections = [];
            for (const sect of div.sections) {
                const filteredProds = sect.products.filter((product) => {
                    const matchesSearch =
                        !strSearch ||
                        product.productName?.toLowerCase().includes(strSearch) ||
                        product.company?.toLowerCase().includes(strSearch) ||
                        matchesMasterFormatSearch(product, strSearch);

                    const matchesRatingSystems =
                        !filtersToSet?.ratingSystems?.length ||
                        filtersToSet.ratingSystems.some((rs) => {
                            const mappedTitle = RATING_SYSTEM_TITLE_MAP[rs.title] ?? rs.title;
                            return product.ratingSystems?.some((rating: any) =>
                                normalizeRating(rating).includes(normalizeRating(mappedTitle)),
                            );
                        });

                    const matchesEpds =
                        !filtersToSet?.epdFilters?.length ||
                        filtersToSet.epdFilters.some((epd) => {
                            let result = product.epdType
                                ?.toLowerCase()
                                .includes(epd.title.replace('-', ' ').toLowerCase());
                            if (epd.title.toLowerCase().includes('optimized')) {
                                result ||= product.epdProgram?.toLowerCase().includes('opt');
                            }
                            return result;
                        });

                    const matchesSMEpds =
                        !filtersToSet?.smEpdsFilters?.length ||
                        filtersToSet.smEpdsFilters.some((smEpd) => {
                            let result =
                                product.epdType
                                    ?.toLowerCase()
                                    .includes(smEpd.title.replace('-', ' ').toLowerCase()) &&
                                product.epdProgram?.toLowerCase().includes('sminds');
                            if (smEpd.title.toLowerCase().includes('optimized')) {
                                result ||=
                                    product.epdProgram?.toLowerCase().includes('opt') &&
                                    product.epdProgram?.toLowerCase().includes('sminds');
                            }
                            return result;
                        });

                    const matchesEC3 =
                        !filtersToSet?.ec3Filters?.length ||
                        filtersToSet.ec3Filters.some((ec3) =>
                            product.ec3EmbodiedCarbon
                                ?.toLowerCase()
                                .includes(ec3.title.toLowerCase()),
                        );

                    const matchesRegulations =
                        !filtersToSet?.regulationsFilters?.length ||
                        filtersToSet.regulationsFilters.some((r) =>
                            product.regulations?.some((regulation) =>
                                regulation.toLowerCase().includes(r.title.toLowerCase()),
                            ),
                        );

                    const matchesMaterialsFilters =
                        !filtersToSet?.materialsFilters?.length ||
                        filtersToSet.materialsFilters.some((mf) =>
                            handleMaterialsFilters(mf, product),
                        );

                    const matchesLowEmitting =
                        !filtersToSet?.lowEmittingFilters?.length ||
                        filtersToSet.lowEmittingFilters.some((lem) => (product as any)[lem.id]);

                    const matchesLemRegulations =
                        !filtersToSet?.lemRegulationsFilters?.length ||
                        filtersToSet.lemRegulationsFilters.some((r) => (product as any)[r.id]);

                    const matchesDate =
                        !filtersToSet?.dateFilters?.length ||
                        filtersToSet.dateFilters.some((dateFilter) =>
                            matchesDateFilter(product.dateAdded, dateFilter),
                        );

                    return (
                        matchesSearch &&
                        matchesRatingSystems &&
                        matchesEpds &&
                        matchesSMEpds &&
                        matchesEC3 &&
                        matchesRegulations &&
                        matchesMaterialsFilters &&
                        matchesLowEmitting &&
                        matchesLemRegulations &&
                        matchesDate
                    );
                });

                if (filteredProds.length) {
                    const filteredSection = { ...sect };
                    filteredSection.clicked = true;
                    filteredSection.products = filteredProds;
                    filteredSections.push(filteredSection);
                }
            }
            if (filteredSections.length) {
                const filteredD = { ...div };
                filteredD.clicked = true;
                filteredD.sections = filteredSections;
                filteredDs.push(filteredD);
            }
        }
        return filteredDs;
    };

    const handleMaterialsFilters = (materialFilter: ProductsFilterData, product: Product) => {
        switch (materialFilter.id) {
            case MaterialsFiltersIds.HPD:
                return Boolean(product.hpd);
            case MaterialsFiltersIds.HPDPW:
                return Boolean(product.perkinsWillPrecautionaryListFree);
            case MaterialsFiltersIds.HPDLBC:
                return Boolean(product.livingBuildingChallengeRedListFree);
            case MaterialsFiltersIds.HPDC2C:
                return Boolean(product.c2cRestrictSubstancesListFree);
            case MaterialsFiltersIds.Declare:
                return Boolean(product.declare);
            case MaterialsFiltersIds.DeclareLBCRedList:
            case MaterialsFiltersIds.DeclareLBCCopliant:
            case MaterialsFiltersIds.DeclareDeclared:
                return product.declareResults
                    ?.toLowerCase()
                    .includes(materialFilter.title.toLowerCase());
            case MaterialsFiltersIds.C2CCertified:
                return Boolean(product.c2cResults);
            case MaterialsFiltersIds.C2CCertifiedPlatinum:
            case MaterialsFiltersIds.C2CCertifiedGold:
            case MaterialsFiltersIds.C2CCertifiedSilver:
            case MaterialsFiltersIds.C2CCertifiedBronze:
                return product.c2cResults
                    ?.toLowerCase()
                    .includes(materialFilter.title.toLowerCase());
            case MaterialsFiltersIds.C2CMHC:
                return Boolean(product.c2cMHCResults);
            case MaterialsFiltersIds.C2CMHCPlatinum:
            case MaterialsFiltersIds.C2CMHCGold:
            case MaterialsFiltersIds.C2CMHCSilver:
            case MaterialsFiltersIds.C2CMHCBronze:
                return product.c2cMHCResults
                    ?.toLowerCase()
                    .includes(materialFilter.title.toLowerCase());
            case MaterialsFiltersIds.LPC:
                return product.lpc?.toLowerCase().includes(materialFilter.title.toLowerCase());
            case MaterialsFiltersIds.MFRI:
                return Boolean(product.mfrInv);
            default:
                return false;
        }
    };

    // ─── Division / section open-close ────────────────────────────────────────

    const handleDivisionOpen = (id: string) => {
        const index = allDivisions.findIndex((d) => d.id === id);
        if (index !== -1) {
            const current = [...allDivisions];
            current[index] = { ...current[index], clicked: !current[index].clicked };
            if (!current[index].clicked) setOpenedSectionsIds([]);
            setAllDivisions(current);
        }
    };

    const handleSectionOpen = (divisionId: string, sectionId: string) => {
        const divIdx = allDivisions.findIndex((d) => d.id === divisionId);
        if (divIdx === -1) return;
        const sectIdx = allDivisions[divIdx].sections.findIndex((s) => s.id === sectionId);
        if (sectIdx === -1) return;
        const current = allDivisions.map((div, di) => {
            if (di !== divIdx) return div;
            const sections = div.sections.map((sect, si) => {
                if (si !== sectIdx) return sect;
                const clicked = !sect.clicked;
                updateOpenedSectionsIds(clicked, sectionId);
                return { ...sect, clicked };
            });
            return { ...div, sections };
        });
        setAllDivisions(current);
    };

    const handleFilteredDivisionOpen = (id: string) => {
        const index = filteredDivisions.findIndex((d) => d.id === id);
        if (index !== -1) {
            const current = [...filteredDivisions];
            current[index] = { ...current[index], clicked: !current[index].clicked };
            if (!current[index].clicked) setOpenedSectionsIds([]);
            setFilteredDivisions(current);
        }
    };

    const handleFilteredSectionOpen = (divisionId: string, sectionId: string) => {
        const divIdx = filteredDivisions.findIndex((d) => d.id === divisionId);
        if (divIdx === -1) return;
        const sectIdx = filteredDivisions[divIdx].sections.findIndex((s) => s.id === sectionId);
        if (sectIdx === -1) return;
        const current = filteredDivisions.map((div, di) => {
            if (di !== divIdx) return div;
            const sections = div.sections.map((sect, si) => {
                if (si !== sectIdx) return sect;
                const clicked = !sect.clicked;
                updateOpenedSectionsIds(clicked, sectionId);
                return { ...sect, clicked };
            });
            return { ...div, sections };
        });
        setFilteredDivisions(current);
    };

    const updateDivisions = (product: Product) => {
        const highestSection = product.mfSectionLevel1 || product.mfSection;
        const update = (divs: Division<Product>[]) =>
            divs.map((div) => {
                if (div.id !== product.mfDivision.number) return div;
                return {
                    ...div,
                    sections: div.sections.map((sect) => {
                        if (sect.id !== highestSection.number.slice(0, 5)) return sect;
                        return {
                            ...sect,
                            products: sect.products.map((p) => (p.id === product.id ? product : p)),
                        };
                    }),
                };
            });
        setAllDivisions(update(allDivisions));
        if (isFilterMode) setFilteredDivisions(update(filteredDivisions));
    };

    const handleDeleteProduct = (product: Product) => {
        const remove = (divs: Division<Product>[]) => {
            const highestSection = product.mfSectionLevel1 || product.mfSection;
            return divs
                .map((div) => {
                    if (div.id !== product.mfDivision.number) return div;
                    const sections = div.sections
                        .map((sect) => {
                            if (sect.id !== highestSection.number.slice(0, 5)) return sect;
                            return {
                                ...sect,
                                products: sect.products.filter((p) => p.id !== product.id),
                            };
                        })
                        .filter((s) => s.products.length > 0);
                    return { ...div, sections };
                })
                .filter((d) => d.sections.length > 0);
        };
        setAllDivisions(remove(allDivisions));
        if (isFilterMode) setFilteredDivisions(remove(filteredDivisions));
    };

    const checkWhatActionNeeded = (divisions: Division<Product>[]) => {
        for (const div of divisions) {
            if (!div.clicked) return true;
            for (const sect of div.sections) {
                if (!sect.clicked) return true;
            }
        }
        return false;
    };

    const openCloseAll = () => {
        if (!isFilterMode) {
            const action = checkWhatActionNeeded(allDivisions);
            const openedIds: string[] = [];
            setAllDivisions(
                allDivisions.map((div) => ({
                    ...div,
                    clicked: action,
                    sections: div.sections.map((sect) => {
                        if (action) openedIds.push(sect.id);
                        return { ...sect, clicked: action };
                    }),
                })),
            );
            setOpenedSectionsIds(openedIds);
        } else {
            const action = checkWhatActionNeeded(filteredDivisions);
            const openedIds: string[] = [];
            setFilteredDivisions(
                filteredDivisions.map((div) => ({
                    ...div,
                    clicked: action,
                    sections: div.sections.map((sect) => {
                        if (action) openedIds.push(sect.id);
                        return { ...sect, clicked: action };
                    }),
                })),
            );
            setOpenedSectionsIds(openedIds);
        }
    };

    const getBrandAndProductCount = (divisions: Division<Product>[]) => {
        let productsCount = 0;
        const companiesNames: string[] = [];
        for (const div of divisions) {
            for (const sect of div.sections) {
                productsCount += sect.products.length;
                sect.products.forEach((p) => {
                    if (p.company) companiesNames.push(p.company);
                });
            }
        }
        return { products: productsCount, companies: [...new Set(companiesNames)].length };
    };

    const selectProduct = (event: CheckboxChangeEvent, product: Product) => {
        if (event.target.checked) {
            setSelectedProducts((prev) => [...prev, product]);
        } else {
            setSelectedProducts((prev) => prev.filter((p) => p.id !== product.id));
        }
    };

    const selectAllProducts = (e: CheckboxChangeEvent) => {
        if (e.target.checked) {
            const source = isFilterMode ? filteredDivisions : allDivisions;
            setSelectedProducts(source.flatMap((d) => d.sections.flatMap((s) => s.products)));
        } else {
            setSelectedProducts([]);
        }
        setSelectAll(e.target.checked);
    };

    const updateOpenedSectionsIds = (clicked: boolean, sectionId: string) => {
        setOpenedSectionsIds((prev) =>
            clicked ? [...prev, sectionId] : prev.filter((id) => id !== sectionId),
        );
    };

    const handleAddToProject = async () => {
        if (!copyProductsTo) {
            return;
        }
        setAddingToProject(true);
        const { payload } = (await dispatch(
            addProductsFromLiveGuidelineAction({
                liveGuidelineId,
                toProjectId: parseInt(copyProductsTo),
                selectedProductsIds: selectedProducts.map((p) => p.productId),
            }),
        )) as ActionResponse<unknown>;

        if (payload?.data) {
            // Full reload, same as "Add from a Project", so the project page loads the new products and tags.
            window.location.href = `/project-info/${copyProductsTo}/0/2`;
            return;
        }
        setAddingToProject(false);
    };

    const handleCriteria = () => {
        // Must go through the same helper as "Edit criteria" so the pbIsLiveGuideline cookie
        // is set; otherwise TC doesn't know to let the user save criteria back to this guideline.
        openCatalogForLiveGuideline(liveGuidelineId, guidelineCriteria);
    };
    const showLoader = state.pending || isFilteringInProgress;

    if (isGuidelineLoading || isSyncInProgress) {
        return (
            <ProductSpin size="large" spinning tip={isSyncInProgress ? 'Syncing products…' : undefined}>
                <ProductsTabContent />
            </ProductSpin>
        );
    }

    if (!guidelineCriteria.length) {
        return (
            <NoCriteriaState>
                {canHideProducts ? (
                    <>
                        <NoCriteriaText>
                            Set Live Guideline criteria to display products
                        </NoCriteriaText>
                        <NoCriteriaButton onClick={handleCriteria}>Get started</NoCriteriaButton>
                    </>
                ) : (
                    <NoCriteriaText>
                        This Live Guideline doesn&apos;t have any products yet.
                    </NoCriteriaText>
                )}
            </NoCriteriaState>
        );
    }

    return (
        <LiveGuidelineProductTagsProvider>
            <BannerWrapper>
                <LiveGuidelineCriteriaBanner
                    criteria={guidelineCriteria}
                    onEditCriteria={() =>
                        openCatalogForLiveGuideline(liveGuidelineId, guidelineCriteria)
                    }
                    canManageGuidelines={canManageGuidelines}
                />
                <LiveGuidelineNotificationBanner
                    liveGuidelineId={liveGuidelineId}
                    onNavigateToNotifications={onNavigateToNotifications}
                />
            </BannerWrapper>
            <ProductsTabPageContainer className={filterModalOpen ? 'shifted' : ''}>
                <ProductsFilterModal
                    open={filterModalOpen}
                    onCancel={() => setFilterModalOpen(false)}
                    onFilter={handleFilters}
                    type="guideline"
                />

                <ProductsTabHeader>
                    <ProductFilterContainer>
                        <ProductsSearch
                            searchString={state.searchString}
                            placeholder={MASTERFORMAT_SEARCH_PLACEHOLDER}
                            changeSearch={changeSearch}
                            cleanFilter={cleanFilter}
                            handleSearchSubmit={submitSearch}
                        />
                        <ProductsFilterButton onClick={() => setFilterModalOpen((prev) => !prev)} />
                    </ProductFilterContainer>
                    {copyProductsTo && (
                        <SpaceEnd>
                            <AddProductButtonContainer>
                                <AddProductButton
                                    id="add-products-button"
                                    className="add-selections-button"
                                    disabled={!selectedProducts.length || addingToProject}
                                    onClick={handleAddToProject}
                                >
                                    Add selections to Project
                                </AddProductButton>
                            </AddProductButtonContainer>
                        </SpaceEnd>
                    )}
                </ProductsTabHeader>

                {isFilterMode && !isFilteringInProgress && (
                    <FoundResultsCount weight={500} size={20} height={24} color={black}>
                        {!filteredDivisions.length ||
                        getBrandAndProductCount(filteredDivisions).products === 0
                            ? 'No results found'
                            : (() => {
                                  const { companies, products } =
                                      getBrandAndProductCount(filteredDivisions);
                                  return `${companies} brand${
                                      companies !== 1 ? 's' : ''
                                  } | ${products} product${products !== 1 ? 's' : ''} found`;
                              })()}
                    </FoundResultsCount>
                )}

                {(!isFilterMode && allDivisions.length) ||
                (isFilterMode && filteredDivisions.length) ? (
                    <>
                        <OpenCloseAllButton onClick={openCloseAll}>
                            <Text weight={400} size={12} height={14} color={blueLink}>
                                Open / close all
                            </Text>
                        </OpenCloseAllButton>
                        {openedSectionsIds.length > 0 && (
                            <SelectAllContainer>
                                <SelectAllCheckbox
                                    onChange={selectAllProducts}
                                    checked={selectAll}
                                />
                                <BoldText weight={400} size={12} height={14} color={blueLink}>
                                    Select all
                                </BoldText>
                            </SelectAllContainer>
                        )}
                    </>
                ) : null}

                {!showLoader ? (
                    <>
                        {!isFilterMode && (
                            <ProductSpin size="large" spinning={ec3Reducer.pending}>
                                <ProductsTabContent>
                                    {allDivisions.length ? (
                                        <DivisionsContainer
                                            className={allDivisions[0].clicked ? 'clicked' : ''}
                                        >
                                            {allDivisions.map((division) => (
                                                <GuidelineDivisionItem
                                                    liveGuidelineId={liveGuidelineId}
                                                    liveGuidelineName={liveGuidelineName}
                                                    division={division}
                                                    key={division.key}
                                                    references={references}
                                                    canHideProducts={canHideProducts}
                                                    editable={canManageGuidelines()}
                                                    brandsInfo={brandsInformation}
                                                    handleClicked={handleDivisionOpen}
                                                    handleSectionOpen={handleSectionOpen}
                                                    handleDeleteProduct={handleDeleteProduct}
                                                    updateDivisions={updateDivisions}
                                                    filterModalOpened={filterModalOpen}
                                                    handleSelectProduct={selectProduct}
                                                    selectedProducts={selectedProducts}
                                                />
                                            ))}
                                        </DivisionsContainer>
                                    ) : (
                                        <NoProductsTitleContainer>
                                            <h1>No products in this guideline yet.</h1>
                                        </NoProductsTitleContainer>
                                    )}
                                </ProductsTabContent>
                            </ProductSpin>
                        )}

                        {isFilterMode && (
                            <ProductSpin size="large" spinning={ec3Reducer.pending}>
                                <ProductsTabContent>
                                    {filteredDivisions.length ? (
                                        <DivisionsContainer
                                            className={
                                                filteredDivisions[0].clicked ? 'clicked' : ''
                                            }
                                        >
                                            {filteredDivisions.map((division) => (
                                                <GuidelineDivisionItem
                                                    liveGuidelineId={liveGuidelineId}
                                                    liveGuidelineName={liveGuidelineName}
                                                    division={division}
                                                    key={division.key}
                                                    references={references}
                                                    canHideProducts={canHideProducts}
                                                    editable={canManageGuidelines()}
                                                    brandsInfo={brandsInformation}
                                                    handleClicked={handleFilteredDivisionOpen}
                                                    handleSectionOpen={handleFilteredSectionOpen}
                                                    handleDeleteProduct={handleDeleteProduct}
                                                    updateDivisions={updateDivisions}
                                                    filterModalOpened={filterModalOpen}
                                                    handleSelectProduct={selectProduct}
                                                    selectedProducts={selectedProducts}
                                                />
                                            ))}
                                        </DivisionsContainer>
                                    ) : (
                                        <NoProductsTitleContainerFiltered>
                                            <center>
                                                <Image
                                                    src="src/assets/images/empty-graphic 1.svg"
                                                    preview={false}
                                                    alt="no results found"
                                                />
                                                <h1>
                                                    <center>
                                                        Clear search field to
                                                        <br />
                                                        display products and try
                                                        <br />
                                                        your search again.
                                                    </center>
                                                </h1>
                                            </center>
                                        </NoProductsTitleContainerFiltered>
                                    )}
                                </ProductsTabContent>
                            </ProductSpin>
                        )}
                    </>
                ) : (
                    <ProductSpin size="large" spinning={showLoader}>
                        <ProductsTabContent />
                    </ProductSpin>
                )}
            </ProductsTabPageContainer>
        </LiveGuidelineProductTagsProvider>
    );
};

export default ProductsTab;
